import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Briefcase, Users, Sparkles, TrendingUp, Mail, ShieldCheck, Pencil, Trash2, ExternalLink } from "lucide-react";
import { MatchRing } from "@/components/MatchRing";
import { EditJobDialog } from "@/components/EditJobDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

/**
 * Admin dashboard — read-only visibility across the entire platform.
 */
export const AdminDashboard = () => {
  const [tab, setTab] = useState("overview");

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <div className="mono-label flex items-center gap-1.5">
        <ShieldCheck className="w-3 h-3 text-[#06B6D4]" /> platform admin
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2">
        Every job, every applicant, one screen.
      </h1>

      <div className="mt-6 inline-flex rounded-full border border-white/10 bg-[#111827] p-1">
        {["overview", "jobs", "applications", "users"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`admin-tab-${t}`}
            className={`text-xs px-4 py-1.5 rounded-full font-mono uppercase transition-colors ${
              tab === t ? "bg-[#06B6D4] text-[#0B0F19]" : "text-slate-400 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview />}
      {tab === "jobs" && <JobsTable />}
      {tab === "applications" && <ApplicationsTable />}
      {tab === "users" && <UsersTable />}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div className="border border-white/10 bg-[#111827] rounded-xl p-5">
    <div className="flex items-center justify-between">
      <div className="mono-label">{label}</div>
      <Icon className="w-4 h-4 text-slate-500" />
    </div>
    <div className="font-display text-3xl mt-3" style={{ color: accent || "#F8FAFC" }}>{value}</div>
  </div>
);

const Overview = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="text-slate-500 mt-6 text-sm">Loading…</div>;
  return (
    <div className="mt-6" data-testid="admin-overview">
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard label="Jobs" value={data.totals.jobs} icon={Briefcase} />
        <StatCard label="Candidates" value={data.totals.candidates} icon={Users} />
        <StatCard label="Recruiters" value={data.totals.recruiters} icon={Users} accent="#06B6D4" />
        <StatCard label="Applications" value={data.totals.applications} icon={Sparkles} />
        <StatCard label="Invites sent" value={data.totals.invites} icon={Mail} />
        <StatCard label="Teams" value={data.totals.teams} icon={Users} />
        <StatCard label="Avg match" value={`${data.totals.avg_match ?? "—"}`} icon={TrendingUp} accent="#10B981" />
      </div>
      <div className="mt-6 border border-white/10 bg-[#111827] rounded-xl p-5">
        <div className="mono-label mb-3">funnel</div>
        <div className="grid grid-cols-5 gap-3 text-center">
          {Object.entries(data.funnel).map(([k, v]) => (
            <div key={k}>
              <div className="text-3xl font-display">{v}</div>
              <div className="mono-label mt-1">{k}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const JobsTable = () => {
  const [jobs, setJobs] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deletingJob, setDeletingJob] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const confirmDelete = async () => {
    if (!deletingJob) return;
    setDeleting(true);
    try {
      await api.delete(`/jobs/${deletingJob.job_id}`);
      toast.success(`Deleted “${deletingJob.title}”`);
      setDeletingJob(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-6 border border-white/10 bg-[#111827] rounded-xl overflow-hidden" data-testid="admin-jobs">
      <div className="grid grid-cols-12 px-4 py-2 border-b border-white/10 mono-label text-slate-500 text-[10px]">
        <div className="col-span-4">Job</div>
        <div className="col-span-3">Company / Location</div>
        <div className="col-span-2">Recruiter</div>
        <div className="col-span-1 text-center">Applicants</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      {jobs.map((j) => (
        <div key={j.job_id} data-testid={`admin-job-${j.job_id}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-white/5 text-sm">
          <div className="col-span-4 min-w-0">
            <div className="text-slate-200 truncate">{j.title}</div>
            <div className="text-[11px] text-slate-500 truncate">{j.job_id}</div>
          </div>
          <div className="col-span-3 min-w-0">
            <div className="text-slate-300 truncate">{j.company}</div>
            <div className="text-[11px] text-slate-500 truncate">{j.location}</div>
          </div>
          <div className="col-span-2 min-w-0">
            <div className="text-slate-300 truncate">{j.recruiter_name || "—"}</div>
            <div className="text-[11px] text-slate-500 truncate">{j.recruiter_email || ""}</div>
          </div>
          <div className="col-span-1 text-center text-slate-200">{j.applicants}</div>
          <div className="col-span-2 flex justify-end gap-1">
            <Link
              to={`/recruiter/jobs/${j.job_id}`}
              data-testid={`admin-job-open-${j.job_id}`}
              title="Open applicants"
              className="p-1.5 rounded hover:bg-white/5 text-slate-400 hover:text-[#06B6D4] transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <button
              onClick={() => setEditingId(j.job_id)}
              data-testid={`admin-job-edit-${j.job_id}`}
              title="Edit job"
              className="p-1.5 rounded hover:bg-white/5 text-slate-400 hover:text-[#3B82F6] transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setDeletingJob(j)}
              data-testid={`admin-job-delete-${j.job_id}`}
              title="Delete job"
              className="p-1.5 rounded hover:bg-white/5 text-slate-400 hover:text-[#F87171] transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
      {jobs.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No jobs yet.</div>}

      <EditJobDialog
        open={!!editingId}
        onOpenChange={(open) => !open && setEditingId(null)}
        jobId={editingId}
        onSaved={load}
      />

      <AlertDialog open={!!deletingJob} onOpenChange={(open) => !open && setDeletingJob(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100" data-testid="admin-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">Delete this job?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              You're about to permanently delete <span className="text-slate-200 font-mono">“{deletingJob?.title}”</span> and all {deletingJob?.applicants || 0} applicant snapshot(s) will lose their linked role. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white" data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="admin-delete-confirm-btn"
              className="bg-[#F87171] hover:bg-[#EF4444] text-white"
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ApplicationsTable = () => {
  const [apps, setApps] = useState([]);
  useEffect(() => { api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => {}); }, []);
  return (
    <div className="mt-6 border border-white/10 bg-[#111827] rounded-xl overflow-hidden" data-testid="admin-applications">
      <div className="grid grid-cols-12 px-4 py-2 border-b border-white/10 mono-label text-slate-500 text-[10px]">
        <div className="col-span-5">Candidate</div>
        <div className="col-span-4">Job</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-1 text-right">Match</div>
      </div>
      {apps.map((a) => (
        <div key={a.application_id} data-testid={`admin-app-${a.application_id}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-white/5 text-sm">
          <div className="col-span-5 min-w-0">
            <div className="text-slate-200 truncate">{a.candidate_name}</div>
            <div className="text-[11px] text-slate-500 truncate">{a.candidate_email}</div>
          </div>
          <div className="col-span-4 text-slate-300 truncate">{a.job_id}</div>
          <div className="col-span-2 text-slate-400 font-mono text-xs uppercase">{a.status || "submitted"}</div>
          <div className="col-span-1 flex justify-end">
            <MatchRing value={a.match?.overall || 0} size={36} stroke={3} />
          </div>
        </div>
      ))}
      {apps.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No applications yet.</div>}
    </div>
  );
};

const UsersTable = () => {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  return (
    <div className="mt-6 border border-white/10 bg-[#111827] rounded-xl overflow-hidden" data-testid="admin-users">
      <div className="grid grid-cols-12 px-4 py-2 border-b border-white/10 mono-label text-slate-500 text-[10px]">
        <div className="col-span-5">Name</div>
        <div className="col-span-5">Email</div>
        <div className="col-span-2">Role</div>
      </div>
      {users.map((u) => (
        <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-white/5 text-sm">
          <div className="col-span-5 text-slate-200 truncate">{u.name}</div>
          <div className="col-span-5 text-slate-400 truncate">{u.email}</div>
          <div className="col-span-2">
            <span className={`text-[10px] px-2 py-0.5 rounded border font-mono uppercase ${
              u.role === "admin" ? "border-[#F59E0B]/40 text-[#F59E0B]" :
              u.role === "recruiter" ? "border-[#06B6D4]/40 text-[#06B6D4]" :
              "border-white/10 text-slate-400"
            }`}>{u.role}</span>
          </div>
        </div>
      ))}
    </div>
  );
};
